import React, { createContext, useContext, useEffect, useCallback, ReactNode } from 'react'
import en from '../locales/en.json'
import fr from '../locales/fr.json'
import es from '../locales/es.json'
import ru from '../locales/ru.json'
import it from '../locales/it.json'
import de from '../locales/de.json'
import pt from '../locales/pt.json'
import hy from '../locales/hy.json'

export type Locale = 'en' | 'fr' | 'es' | 'ru' | 'it' | 'de' | 'pt' | 'hy'

const STORAGE_KEY = 'restoside-locale'

const LOCALE_FLAGS: Record<Locale, string> = {
  en: '🇬🇧',
  fr: '🇫🇷',
  es: '🇪🇸',
  ru: '🇷🇺',
  it: '🇮🇹',
  de: '🇩🇪',
  pt: '🇵🇹',
  hy: '🇦🇲',
}

const LOCALE_NAMES: Record<Locale, string> = {
  en: 'English',
  fr: 'Français',
  es: 'Español',
  ru: 'Русский',
  it: 'Italiano',
  de: 'Deutsch',
  pt: 'Português',
  hy: 'Հայերեն',
}

interface LanguageContextType {
  locale: Locale
  setLocale: (locale: Locale) => void
  t: (key: string, params?: Record<string, string>) => string
  flags: typeof LOCALE_FLAGS
  localeNames: typeof LOCALE_NAMES
}

const messagesMap: Record<Locale, Record<string, string>> = { en, fr, es, ru, it, de, pt, hy }

const LanguageContext = createContext<LanguageContextType>({
  locale: 'en',
  setLocale: () => {},
  t: (key: string) => key,
  flags: LOCALE_FLAGS,
  localeNames: LOCALE_NAMES,
})

const DEFAULT_LOCALE: Locale = 'en'

const getStoredLocale = (): Locale => {
  if (typeof window === 'undefined') return DEFAULT_LOCALE
  const stored = localStorage.getItem(STORAGE_KEY)
  if (stored && messagesMap[stored as Locale]) return stored as Locale
  return DEFAULT_LOCALE
}

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [locale, setLocaleState] = React.useState<Locale>(getStoredLocale)
  const messages = messagesMap[locale] || messagesMap.en

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, locale)
    document.documentElement.lang = locale
  }, [locale])

  const setLocale = useCallback((next: Locale) => {
    if (messagesMap[next]) setLocaleState(next)
  }, [])

  const t = useCallback(
    (key: string, params?: Record<string, string>): string => {
      let value = messages[key] ?? messagesMap.en[key] ?? key
      if (params) {
        Object.keys(params).forEach((k) => {
          value = value.replace(new RegExp(`{{${k}}}`, 'g'), params[k])
        })
      }
      return value
    },
    [messages],
  )

  return (
    <LanguageContext.Provider
      value={{
        locale,
        setLocale,
        t,
        flags: LOCALE_FLAGS,
        localeNames: LOCALE_NAMES,
      }}
    >
      {children}
    </LanguageContext.Provider>
  )
}

export const useTranslation = () => useContext(LanguageContext)
export { LOCALE_FLAGS, LOCALE_NAMES }
